import React, { useState } from 'react';
import { api } from './api.js';
import { useLang } from './i18n.js';
import { Overlay, Chip } from './ui.jsx';

/**
 * Quick log.
 *
 * The load-bearing habit, and the one v2 lost: logging a call took a modal on a
 * desktop-shaped page, so nobody did it from a taxi and the data went stale.
 * This is reachable from anywhere (⌘K / the + tab on mobile), pre-fills its
 * target when opened from a drawer, and writes the activity and its follow-up
 * task in a single request.
 */
export function QuickLog({ target, onClose, onDone, toast }) {
  const { t } = useLang();
  const [type, setType] = useState('call');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const types = [
    ['call', t('call')],
    ['meet', t('meet')],
    ['email', t('email')],
    ['line', t('line')],
    ['note', t('note')],
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!target?.deal_id && !target?.company_id) {
      setError(t('pickTarget'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.logActivity({
        type,
        subject: subject || types.find((x) => x[0] === type)[1],
        body,
        deal_id: target.deal_id ?? null,
        company_id: target.company_id ?? null,
        ...(followUp ? { follow_up: followUp, follow_up_due: due || undefined } : {}),
      });
      toast(t('logged'));
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose} kind="modal">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '18px 20px 10px 20px' }}>
          <div className="display" style={{ fontSize: 18 }}>
            {t('logActivity')}
          </div>
          {target?.label && (
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
              {t('against')} {target.label}
            </div>
          )}
        </div>

        <div style={{ padding: '0 20px', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {types.map(([v, label]) => (
              <Chip key={v} on={type === v} onClick={() => setType(v)}>
                {label}
              </Chip>
            ))}
          </div>

          <label className="field-label">{t('subject')}</label>
          <input
            className="input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            autoFocus
          />

          <label className="field-label" style={{ marginTop: 12 }}>
            {t('details')}
          </label>
          <textarea
            className="textarea"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />

          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: '1px solid var(--line)',
            }}
          >
            <label className="field-label">{t('followUp')}</label>
            <input
              className="input"
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              placeholder={t('followUpPh')}
            />
            {followUp && (
              <>
                <label className="field-label" style={{ marginTop: 10 }}>
                  {t('due')}
                </label>
                <input
                  className="input"
                  type="date"
                  value={due}
                  onChange={(e) => setDue(e.target.value)}
                />
              </>
            )}
          </div>

          {error && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 12 }}>{error}</div>
          )}
        </div>

        <div className="drawer-foot" style={{ marginTop: 14 }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
            {t('cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy}>
            {t('logIt')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}
